import { useEffect } from 'react';
import { getSalonBoot } from '../utils/salonBoot';

/**
 * THE BROWSER TAB, NAMED BY THE PAGE THAT IS SHOWING (DK parity, S1c).
 *
 * The titles registered by the pages that are mounted, newest last. `RtlSync`
 * writes the platform's "Stylon" only while this is empty, so a language change
 * cannot put the platform's name back on a shop's tab. When the last page goes,
 * the tab gets back the business the page was served for (`businessTabTitle`),
 * else whatever it said before the first one came.
 */
const stack = [];
let before = null;

export function hasPageTitle() {
  return stack.length > 0;
}

/**
 * THE BUSINESS THIS PAGE WAS SERVED FOR, or '' on Stylon's own pages.
 *
 * Seen on 2026-09-17: a DK Store product page arrived titled "<product> | DK
 * Store", and the tab read "Stylon" — `RtlSync` wrote the platform's name before
 * the page had loaded anything to name the tab with, and a page that names
 * nothing gave the tab back "Stylon" or the last product's name. The server
 * stamps the business's name into `window.__SALON_BOOT__` on every page it
 * resolved to a business (server-helpers.js), before the bundle runs, so an
 * unnamed tab on such a page says the business. Only a page served for no
 * business says "Stylon".
 */
export function businessTabTitle() {
  return String(getSalonBoot()?.name || '').trim();
}

/**
 * The shop's HOME page names the tab after the shop for every shop — the
 * ad-landing shop and its product pages read as that business, switch or no
 * switch. A category's page still waits for the switch (`shopTabTitle`).
 */
export function shopHomeTabTitle(salon) {
  return oneLine(salon?.name) || null;
}

/**
 * "{name} | {shop}", or the shop alone when the page names nothing — and null,
 * so that nothing is written at all, unless the shop's owner turned
 * `shopTabTitlesEnabled` on. A shop that never asked keeps its tab as it is.
 */
export function shopTabTitle(salon, entityName) {
  if (salon?.shopTabTitlesEnabled !== true) return null;
  const base = String(salon?.name || '').trim();
  if (!base) return null;
  const name = String(entityName || '').trim();
  return name ? `${name} | ${base}` : base;
}

/** Runs of space as one, trimmed — `oneLine` in server-shop-meta.js, which writes a head's title. */
function oneLine(text) {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

/**
 * A PRODUCT PAGE'S TAB: "{product} | {shop}" for every shop, switch or no switch — the title the
 * page's own head arrives with (`buildProductPreview` in server-shop-meta.js), held equal to it by
 * hooks/__tests__/aTabReadsTheNameItsLinkPreviewReads.test.js. The product name alone when the
 * shop has none.
 *
 * Seen live on 2026-09-17: a DK Store product page arrived with "<product> | DK Store" in its head,
 * and once loaded the tab read "Stylon" — the page named its tab only for a shop that turned tab
 * titles on, and `RtlSync` puts the platform's name in any tab no page has named.
 *
 * Until the product has a name and the shop has loaded it is what every shop page says
 * (`shopTabTitle`): the shop's own name where the owner asked, else nothing.
 */
export function productTabTitle(salon, productName) {
  const name = oneLine(productName);
  if (!name || !salon) return shopTabTitle(salon, productName);
  const shop = oneLine(salon.name);
  return shop ? `${name} | ${shop}` : name;
}

/**
 * A collection's or a product's name in the reader's language, read exactly as
 * its link preview reads it (`shopEntityText` in server-shop-meta.js, which is
 * `courseText` in server-helpers.js): `translations[lang].name`, trimmed, else
 * the flat `name`, trimmed. The storefront cannot import that CommonJS helper,
 * so this is its copy, held equal to it by
 * hooks/__tests__/aTabReadsTheNameItsLinkPreviewReads.test.js.
 *
 * Not `getDisplayNameForLocale`, which also reads legacy nameAr/nameHe columns
 * and falls back to the id — words the preview never shows.
 */
export function shopEntityName(entity, language) {
  if (!entity || typeof entity !== 'object') return '';
  const lang = String(language || 'en').toLowerCase().split(/[-_]/)[0];
  const translations = entity.translations;
  if (translations && typeof translations === 'object') {
    const own = translations[lang];
    if (own && typeof own === 'object' && typeof own.name === 'string' && own.name.trim()) {
      return own.name.trim();
    }
  }
  return typeof entity.name === 'string' ? entity.name.trim() : '';
}

export default function useDocumentTitle(title) {
  useEffect(() => {
    if (!title || typeof document === 'undefined') return undefined;
    if (stack.length === 0) before = document.title;
    const entry = { title };
    stack.push(entry);
    document.title = title;
    return () => {
      const i = stack.indexOf(entry);
      if (i !== -1) stack.splice(i, 1);
      document.title = stack.length ? stack[stack.length - 1].title : (businessTabTitle() || before);
    };
  }, [title]);
}
